import type { ReactNode } from "react";
import { InfoIcon, XIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "../../ui/popover";
import { ComposerBanner } from "./ComposerBanner";
import type { ComposerBannerStackItem } from "./ComposerBannerStack";

export function ComposerBannerStackAlert({
  item,
  attached,
  onDismissRequest,
}: {
  readonly item: ComposerBannerStackItem;
  readonly attached: boolean;
  readonly onDismissRequest: (control: HTMLButtonElement) => void;
}) {
  return (
    <ComposerBanner.Root
      role={item.variant === "error" ? "alert" : "status"}
      placement={attached ? "attached" : "floating"}
      variant={item.variant}
      density="comfortable"
    >
      <ComposerBanner.Row layout="wrap-actions-narrow">
        <ComposerBanner.Icon className="h-(--composer-banner-icon-column) self-start">
          {item.icon}
        </ComposerBanner.Icon>
        <ComposerBanner.Content className="whitespace-nowrap">
          <span
            className={cn(
              "min-w-0 font-medium leading-7 sm:leading-6",
              typeof item.title === "string" && "truncate",
            )}
          >
            {item.title}
          </span>
          {item.description ? <NoticeDescription>{item.description}</NoticeDescription> : null}
        </ComposerBanner.Content>
        {item.actions || item.onDismiss ? (
          <ComposerBanner.Actions>
            {item.actions}
            {item.onDismiss ? (
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                aria-label={item.dismissLabel ?? "Dismiss notice"}
                onClick={(event) => onDismissRequest(event.currentTarget)}
              >
                <XIcon />
              </Button>
            ) : null}
          </ComposerBanner.Actions>
        ) : null}
      </ComposerBanner.Row>
      {item.children ? <ComposerBanner.Children>{item.children}</ComposerBanner.Children> : null}
    </ComposerBanner.Root>
  );
}

function NoticeDescription({ children }: { readonly children: ReactNode }) {
  return (
    <>
      <span className="min-w-0 shrink-[9999] truncate text-muted-foreground @max-[400px]:sr-only">
        {children}
      </span>
      <Popover>
        <PopoverTrigger
          openOnHover
          render={
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label="Show notice details"
              className="hidden flex-none text-muted-foreground hover:text-foreground @max-[400px]:inline-flex"
            />
          }
        >
          <InfoIcon className="size-3.5" />
        </PopoverTrigger>
        <PopoverPopup tooltipStyle side="top" className="max-w-72 whitespace-normal text-pretty">
          {children}
        </PopoverPopup>
      </Popover>
    </>
  );
}
